import { useState } from 'react';
import { App, ColorPicker, Drawer, Segmented, Tooltip } from 'antd';
import { CheckOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';

import { Badge, Button, Input } from '../ui';
import { useTheme } from '../ThemeProvider';
import { usePhamVi } from '../../hooks/usePhamVi';
import { useT } from '../../i18n';
import { useQuanTriToChuc } from '../../modules/ho-so-to-chuc/hooks/useQuanTriToChuc';
import { TabToChuc } from '../../modules/ho-so-to-chuc/components/TabToChuc';
import {
  layMauNhan,
  MA_MAU_TUY_Y,
  MAU_NHAN,
  MUC_BO_GOC,
  TUY_CHINH_MAC_DINH,
} from '../../styles/tuy-chinh';
import { cn } from '../../lib/cn';

/**
 * Ngăn tùy chỉnh giao diện (ADR-UI-0007).
 *
 * Là **ngăn trượt**, không phải một màn riêng: đổi màu hay bo góc là việc
 * người dùng làm *trong lúc* đang xem một màn khác, và họ cần thấy ngay nó
 * ảnh hưởng thế nào tới chính màn đó. Đẩy sang một màn riêng thì mất ngữ cảnh và
 * mất cả chỗ làm việc đang dở.
 *
 * ADR-UI-0016: có hai tab. "Cá nhân" (ai cũng thấy) lưu ở máy này; "Tổ chức"
 * chỉ hiện với quản trị tổ chức (`TENANT_ADMIN`) ở cổng tenant, lưu lên
 * server cho mọi thành viên. Người không có quyền không thấy tab, không thấy
 * hàng chọn tab, và không bị tải hồ sơ tổ chức.
 *
 * Vì vậy ngăn này **không có khung xem trước**: cả ứng dụng phía sau đã là
 * khung xem trước. Chỉ giữ một dải mẫu nhỏ cho các thành phần không chắc có
 * mặt trên màn đang mở.
 */
export function NganTuyChinh({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { theme, setTheme, tuyChinh, setTuyChinh } = useTheme();
  const { t } = useT();
  const { modal } = App.useApp();

  // Hai điều kiện, không thay nhau: vai trò nói "được sửa", cổng nói "có tổ chức để sửa".
  const coQuyenSuaToChuc = useQuanTriToChuc();
  const congTenant = usePhamVi() === 'tenant';
  const hienTabToChuc = coQuyenSuaToChuc && congTenant;

  const [tab, setTab] = useState<'ca-nhan' | 'to-chuc'>('ca-nhan');
  // Tên sửa dở ở tab Tổ chức. `lan` đổi để dựng lại tab sau khi người dùng bỏ thay đổi.
  const [chuaLuu, setChuaLuu] = useState(false);
  const [lan, setLan] = useState(0);
  // Mất quyền giữa chừng (đổi tài khoản) thì quay về tab Cá nhân thay vì để trống.
  const tabDangMo = hienTabToChuc ? tab : 'ca-nhan';

  const dong = () => {
    if (!chuaLuu) {
      onClose();
      return;
    }
    modal.confirm({
      title: t('hoSoToChuc.chuaLuu.tieuDe'),
      content: t('hoSoToChuc.chuaLuu.than'),
      okText: t('hoSoToChuc.chuaLuu.boThayDoi'),
      okButtonProps: { danger: true },
      cancelText: t('chung.huy'),
      onOk: () => {
        setChuaLuu(false);
        setLan((n) => n + 1);
        onClose();
      },
    });
  };

  /** Nhãn kèm `(mặc định)` cho lựa chọn đang là mặc định. */
  const nhanKemMacDinh = (khoa: Parameters<typeof t>[0], laMacDinh: boolean) =>
    laMacDinh ? t('hauTo.macDinh', { nhan: t(khoa) }) : t(khoa);

  const dungMauTuyY = tuyChinh.mauNhan === MA_MAU_TUY_Y;
  const mauDangDung = layMauNhan(tuyChinh);

  const laMacDinh =
    tuyChinh.mauNhan === TUY_CHINH_MAC_DINH.mauNhan &&
    tuyChinh.boGoc === TUY_CHINH_MAC_DINH.boGoc;

  return (
    <Drawer
      open={open}
      onClose={dong}
      title={t('tuyChinh.tieuDe')}
      placement="right"
      /* 360px: đủ cho lưới màu 4 cột mà không che hết nội dung phía sau. */
      width={360}
      footer={
        tabDangMo === 'to-chuc' ? (
          <span className="text-2xs text-content-muted">{t('hoSoToChuc.apDungToanTo')}</span>
        ) : (
        <div className="flex items-center justify-between gap-2">
          <span className="text-2xs text-content-muted">{t('tuyChinh.chiLuuTrenMayNay')}</span>
          <Button
            variant="secondary"
            size="sm"
            disabled={laMacDinh}
            onClick={() => setTuyChinh(TUY_CHINH_MAC_DINH)}
          >
            {t('tuyChinh.traVeMacDinh')}
          </Button>
        </div>
        )
      }
    >
      {hienTabToChuc && (
        <Segmented
          block
          className="mb-5"
          value={tabDangMo}
          onChange={(v) => setTab(v as 'ca-nhan' | 'to-chuc')}
          options={[
            { value: 'ca-nhan', label: t('tuyChinh.tabCaNhan') },
            { value: 'to-chuc', label: t('tuyChinh.tabToChuc') },
          ]}
        />
      )}

      {/* Hai ngăn cùng được dựng, ngăn không mở chỉ bị ẩn: đổi tab không làm mất tên đang sửa dở. */}
      {hienTabToChuc && (
        <div hidden={tabDangMo !== 'to-chuc'}>
          <TabToChuc key={lan} onDirty={setChuaLuu} />
        </div>
      )}

      <div className="space-y-6" hidden={tabDangMo !== 'ca-nhan'}>
        <Muc nhan={t('tuyChinh.cheDoNen')}>
          <Segmented
            block
            value={theme}
            onChange={(v) => setTheme(v as 'light' | 'dark')}
            options={[
              { value: 'light', label: t('tuyChinh.nenSang'), icon: <SunOutlined /> },
              { value: 'dark', label: t('tuyChinh.nenToi'), icon: <MoonOutlined /> },
            ]}
          />
        </Muc>

        <Muc nhan={t('tuyChinh.mauNhan')} mo={t('tuyChinh.mauNhanMo')}>
          <div
            role="radiogroup"
            aria-label={t('tuyChinh.mauNhan')}
            className="grid grid-cols-4 gap-2"
          >
            {MAU_NHAN.map((mau) => {
              const chon = tuyChinh.mauNhan === mau.ma;
              const nhan = nhanKemMacDinh(mau.khoa, mau.ma === TUY_CHINH_MAC_DINH.mauNhan);
              return (
                <Tooltip key={mau.ma} title={nhan}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={chon}
                    aria-label={nhan}
                    onClick={() => setTuyChinh({ ...tuyChinh, mauNhan: mau.ma })}
                    style={{ background: mau.base }}
                    className={cn(
                      'grid h-10 w-full place-items-center rounded-lg text-white transition-transform',
                      'hover:scale-105',
                      chon && 'ring-2 ring-offset-2 ring-offset-[var(--bg-elevated)]',
                    )}
                  >
                    {chon && <CheckOutlined aria-hidden />}
                  </button>
                </Tooltip>
              );
            })}
          </div>

          {/*
            Màu tự chọn nằm dưới lưới chứ không phải ô thứ chín: bộ chọn màu mở
            bảng riêng, không cùng kiểu thao tác "bấm là xong" với tám ô trên.
            Viền đậm khi đang dùng để người dùng biết màu hiện tại đến từ đâu.
          */}
          <div
            role="group"
            aria-label={t('tuyChinh.mauTuyY')}
            className={cn(
              'mt-3 flex items-center justify-between gap-3 rounded-lg border px-3 py-2',
              dungMauTuyY ? 'border-primary-600 bg-primary-50' : 'border-default',
            )}
          >
            <span className="min-w-0 text-xs font-medium text-content-secondary">
              {t('tuyChinh.mauTuyY')}
            </span>
            <ColorPicker
              value={tuyChinh.mauTuyY ?? mauDangDung.base}
              format="hex"
              disabledAlpha
              showText
              aria-label={t('tuyChinh.mauTuyYNhan')}
              onChange={(c) =>
                setTuyChinh({
                  ...tuyChinh,
                  mauNhan: MA_MAU_TUY_Y,
                  mauTuyY: c.toHexString().toUpperCase(),
                })
              }
            />
          </div>
          <p className="mt-1.5 text-2xs text-content-muted">{t('tuyChinh.mauTuyYMo')}</p>
          {dungMauTuyY && mauDangDung.daChinh && (
            <p role="status" className="mt-1 text-2xs text-warning-700">
              {t('tuyChinh.daLamDam', { ma: mauDangDung.base })}
            </p>
          )}
        </Muc>

        <Muc nhan={t('tuyChinh.boGoc')} mo={t('tuyChinh.boGocMo')}>
          <div
            role="radiogroup"
            aria-label={t('tuyChinh.boGoc')}
            className="grid grid-cols-5 gap-2"
          >
            {MUC_BO_GOC.map((m) => {
              const chon = tuyChinh.boGoc === m.ma;
              const nhan = nhanKemMacDinh(m.khoa, m.ma === TUY_CHINH_MAC_DINH.boGoc);
              return (
                <Tooltip key={m.ma} title={nhan}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={chon}
                    aria-label={nhan}
                    onClick={() => setTuyChinh({ ...tuyChinh, boGoc: m.ma })}
                    className={cn(
                      'flex h-12 flex-col items-center justify-center gap-1 rounded-lg border transition-colors',
                      chon
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-default text-content-muted hover:bg-subtle',
                    )}
                  >
                    {/* Ô vuông nhỏ mang đúng bán kính của mức này: xem là hiểu. */}
                    <span
                      aria-hidden
                      style={{ borderTopLeftRadius: m.px, borderTopRightRadius: m.px }}
                      className={cn(
                        'h-4 w-6 border-x border-t',
                        chon ? 'border-primary-600' : 'border-strong',
                      )}
                    />
                    <span className="text-2xs tabular-nums">{m.px}</span>
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </Muc>

        <Muc nhan={t('tuyChinh.mauNhanh')} mo={t('tuyChinh.mauNhanhMo')}>
          <div className="space-y-2.5 rounded-lg border border-default bg-subtle p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="primary" size="sm">
                {t('tuyChinh.nutChinh')}
              </Button>
              <Button variant="secondary" size="sm">
                {t('tuyChinh.nutPhu')}
              </Button>
              <Button variant="destructive" size="sm">
                {t('chung.xoa')}
              </Button>
            </div>
            <Input size="small" placeholder={t('tuyChinh.oNhap')} />
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="primary">{t('tuyChinh.nhanMauNhan')}</Badge>
              <Badge variant="success">{t('tuyChinh.nhanXong')}</Badge>
              <Badge variant="warning">{t('tuyChinh.nhanCho')}</Badge>
              <Badge variant="danger">{t('tuyChinh.nhanLoi')}</Badge>
            </div>
          </div>
        </Muc>
      </div>
    </Drawer>
  );
}

/** Một nhóm thiết lập: nhãn, mô tả tùy chọn, rồi phần điều khiển. */
function Muc({
  nhan,
  mo,
  children,
}: {
  nhan: string;
  mo?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-content-secondary">
        {nhan}
      </h3>
      {mo && <p className="mb-2 mt-0.5 text-2xs text-content-muted">{mo}</p>}
      <div className={cn(!mo && 'mt-2')}>{children}</div>
    </section>
  );
}
